<template>
    <Disclosure as="nav" class="sticky top-0 z-40 border-b border-gray-100 bg-white/90 backdrop-blur-sm" v-slot="{ open }">
        <div class="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
            <div class="relative flex h-20 items-center justify-between">
                <div class="absolute inset-y-0 left-0 flex items-center sm:hidden">
                    <!-- Mobile menu button-->
                    <DisclosureButton
                        class="relative inline-flex items-center justify-center rounded-xl p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2">
                        <span class="absolute -inset-0.5"></span>
                        <span class="sr-only">Open main menu</span>
                        <Bars3Icon v-if="!open" class="block size-6" aria-hidden="true" />
                        <XMarkIcon v-else class="block size-6" aria-hidden="true" />
                    </DisclosureButton>
                </div>
                <div class="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
                    <a href="/" class="flex shrink-0 items-center">
                        <img class="h-10 w-auto object-contain" src="/images/logo.png" alt="logo" />
                    </a>
                    <div class="hidden sm:ml-8 sm:block">
                        <div class="flex h-full items-center space-x-1">
                            <Menu as="div" class="relative flex items-center justify-start text-left">
                                <div v-for="item in navigation" :key="item.name">
                                    <router-link :to="item.href" v-if="!item.dropdown"
                                        :class="[
                                            item.current ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900',
                                            'rounded-lg px-3.5 py-2 text-sm font-medium transition-colors'
                                        ]" :aria-current="item.current ? 'page' : undefined">
                                        {{ item.name }}
                                        <ChevronDownIcon v-if="item.dropdown"
                                            class="ml-2 -mr-1 inline-block h-5 w-5 text-gray-400" aria-hidden="true" />
                                    </router-link>

                                    <div v-else class="relative">
                                        <MenuButton
                                            :class="[item.current ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900', 'rounded-lg px-3.5 py-2 text-sm font-medium transition-colors']"
                                            :aria-current="item.current ? 'page' : undefined">
                                            {{ item.name }}
                                            <ChevronDownIcon class="ml-2 -mr-1 inline-block h-5 w-5 text-gray-400"
                                                aria-hidden="true" />
                                        </MenuButton>
                                        <Transition enter="transition ease-out duration-100"
                                            enter-from="transform opacity-0 scale-95"
                                            enter-to="transform opacity-100 scale-100"
                                            leave="transition ease-in duration-75"
                                            leave-from="transform opacity-100 scale-100"
                                            leave-to="transform opacity-0 scale-95">
                                            <MenuItems
                                                class="absolute right-0 z-50 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-xl bg-white p-1 shadow-soft ring-1 ring-gray-100 focus:outline-none">
                                                <div class="px-1 py-1">
                                                    <MenuItem v-slot="{ active }" v-for="subItem in item.dropdown.items"
                                                        :key="subItem.name">
                                                        <router-link :to="subItem.href" :class="[
                                                            active ? 'bg-gray-50 text-gray-900' : 'text-gray-700',
                                                            'group flex w-full items-center rounded-lg px-3 py-2 text-sm transition-colors'
                                                        ]">
                                                            {{ subItem.name }}
                                                        </router-link>
                                                    </MenuItem>
                                                </div>
                                            </MenuItems>
                                        </Transition>
                                    </div>
                                </div>
                            </Menu>
                        </div>
                    </div>
                </div>
                <div v-if="user">
                    <Menu as="div" class="relative flex items-center justify-start text-left">
                        <div>
                            <div class="relative">
                                <MenuButton
                                    class="flex items-center rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2">
                                    <img class="h-9 w-9 rounded-full ring-2 ring-white shadow-soft"
                                        :src="user.avatar || '/images/default-avatar.png'" alt="User Avatar" />
                                    <ChevronDownIcon class="ml-2 -mr-1 inline-block h-5 w-5 text-gray-400"
                                        aria-hidden="true" />
                                </MenuButton>
                                <Transition enter="transition ease-out duration-100"
                                    enter-from="transform opacity-0 scale-95" enter-to="transform opacity-100 scale-100"
                                    leave="transition ease-in duration-75" leave-from="transform opacity-100 scale-100"
                                    leave-to="transform opacity-0 scale-95">
                                    <MenuItems
                                        class="absolute right-0 z-50 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-xl bg-white p-1 shadow-soft ring-1 ring-gray-100 focus:outline-none">
                                        <div class="px-1 py-1">
                                            <MenuItem v-slot="{ active }" v-for="item in userDropdownItems"
                                                :key="item.name">
                                            <a :href="item.href" @click="item.name === 'Logout' ? handleLogout($event) : null" :class="[
                                                active ? 'bg-gray-50 text-gray-900' : 'text-gray-700',
                                                'group flex w-full items-center rounded-lg px-3 py-2 text-sm transition-colors'
                                            ]">
                                                <component :is="item.icon" class="mr-2 h-5 w-5 text-gray-400" />
                                                <span>{{ item.name }}</span>
                                            </a>
                                            </MenuItem>
                                        </div>
                                    </MenuItems>
                                </Transition>
                            </div>
                        </div>
                    </Menu>
                </div>
                <div v-else class="hidden sm:flex sm:items-center sm:justify-end sm:gap-2">
                    <router-link to="/login">
                        <Button outlined size="small" label="Login" />
                    </router-link>
                    <router-link to="/register">
                        <Button size="small" label="Register" />
                    </router-link>
                </div>
            </div>
        </div>

        <DisclosurePanel class="sm:hidden">
            <div class="space-y-1 border-t border-gray-100 px-2 pt-4 pb-3">
                <DisclosureButton v-for="item in navigation" :key="item.name" as="a" :href="item.href"
                    :class="[item.current ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900', 'block rounded-lg px-3 py-2 text-base font-medium transition-colors']"
                    :aria-current="item.current ? 'page' : undefined">{{ item.name }}</DisclosureButton>
            </div>
        </DisclosurePanel>
    </Disclosure>
</template>

<script setup>
import { Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import {
    Bars3Icon,
    BellIcon,
    XMarkIcon,
    AcademicCapIcon,
    UserIcon,
    ArrowLeftEndOnRectangleIcon
} from '@heroicons/vue/24/outline';
import { ChevronDownIcon } from '@heroicons/vue/20/solid';
import Button from 'primevue/button';
import { useAuth } from '@/composables/useAuth';
import { onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { getFeaturedCategories } from '@/services/categoryService';
import { ref } from 'vue';

const { getUser, logout } = useAuth();
const router = useRouter();
const user = ref(null);
const categories = ref([]);

const navigation = [
    {
        name: 'Programs', href: '#', current: false, dropdown: {
            items: []
        }
    },
    { name: 'Books', href: '/books', current: false, dropdown: false },
    { name: 'About Us', href: '/about-us', current: false, dropdown: false },
    { name: 'Testimonial', href: '/#testimonials', current: false, dropdown: false },
    { name: 'Contact Us', href: '/contact-us', current: false, dropdown: false },
    { name: 'Privacy Policy', href: '/privacy-policy', current: false, dropdown: false },
];

const userDropdownItems = [
    { name: 'Account', href: '/account', icon: UserIcon },
    { name: 'Learning', href: '/learning', icon: AcademicCapIcon },
    { name: 'Logout', href: '#', icon: ArrowLeftEndOnRectangleIcon }
];

const handleLogout = async (event) => {
    event.preventDefault();
    await logout();
    user.value = null;
    router.push('/');
};

onMounted(() => {
    getUser()
        .then(userData => {
            user.value = userData;
        })
        .catch(error => {
            // console.error('Error fetching user data:', error);
        });

    getFeaturedCategories().then(data => {
        categories.value = data.data;

        navigation[0].dropdown.items = categories.value.map(category => ({
            name: category.name,
            href: `/courses?category=${category.slug}`
        }));
    });
})
</script>
